/**
 * ConcentrationRisksSummary - Concentration risks summary section
 * Extracted from ConsolidatedHoldings.tsx (TASK-371 decomposition)
 *
 * Renders the red-themed panel listing positions with
 * concentration risk, showing values and risk badges.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Badge from '../atoms/Badge';

export interface ConcentrationRisk {
  name: string;
  value: number;
  percentOfTotal: number;
  riskLevel: string;
  portfolios: string[];
}

export interface ConcentrationRisksSummaryProps {
  concentrationRisks: ConcentrationRisk[];
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `CHF ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `CHF ${(value / 1000).toFixed(0)}K`;
  return `CHF ${value.toLocaleString('de-CH')}`;
};

const getRiskBadge = (risk?: string) => {
  if (!risk) return null;

  switch (risk.toUpperCase()) {
    case 'HIGH':
      return <Badge label="HIGH RISK" variant="error" size="sm" />;
    case 'MEDIUM':
      return <Badge label="MEDIUM" variant="warning" size="sm" />;
    default:
      return <Badge label="LOW" variant="success" size="sm" />;
  }
};

const ConcentrationRisksSummary: React.FC<ConcentrationRisksSummaryProps> = ({
  concentrationRisks,
}) => {
  if (!concentrationRisks || concentrationRisks.length === 0) return null;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-6 h-6 text-red-600" />
        <h3 className="text-lg font-semibold text-red-900">Concentration Risks</h3>
      </div>

      <div className="space-y-2">
        {concentrationRisks.map((risk, idx) => (
          <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-200">
            <div className="flex-1">
              <div className="font-medium text-gray-900">{risk.name}</div>
              <div className="text-xs text-gray-600 mt-0.5">
                In {risk.portfolios.length} portfolio{risk.portfolios.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="text-right mr-4">
              <div className="font-bold text-gray-900">{formatCurrency(risk.value)}</div>
              <div className="text-sm font-semibold text-red-600">{risk.percentOfTotal.toFixed(1)}% of total</div>
            </div>
            {getRiskBadge(risk.riskLevel)}
          </div>
        ))}
      </div>

      <div className="mt-4 bg-red-100 rounded-md p-3">
        <p className="text-sm text-red-800">
          <strong>Recommendation:</strong> Positions exceeding 10% of total wealth should be monitored closely.
          Consider rebalancing to reduce concentration risk.
        </p>
      </div>
    </div>
  );
};

export default ConcentrationRisksSummary;
